<template>
    <div
        ref="sideNavContent"
        class="d-flex flex-column pt-3 side-nav-content"
        :class="[
            isCollapsed ? 'px-1 collapsed-rail' : 'px-3',
            isSidebarDesktop
                ? 'side-nav-content--desktop-light'
                : 'side-nav-content--mobile side-nav-content--mobile-light'
        ]"
        @scroll.passive="updateScrollCue"
    >
        <div
            ref="sidebarHeader"
            class="d-flex align-items-center mb-2 sidebar-header"
            :class="[isCollapsed ? 'justify-content-center' : 'justify-content-between pb-2']"
            style="min-height: 2rem"
        >
            <router-link
                v-if="!isCollapsed"
                to="/"
                class="sidebar-logo-link"
                aria-label="Dashboard"
                @click="hideNavCanvas"
            >
                <img
                    :src="textLogoBlack"
                    alt="ForkTo"
                    :class="['logo-image', isSidebarDesktop ? 'ps-2' : '']"
                    style="height: 1.75rem; padding-top: 0.15rem; padding-bottom: 0.15rem"
                />
            </router-link>
            <button
                v-if="!isSidebarDesktop"
                type="button"
                class="btn-close side-nav-close"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                style="flex-shrink: 0"
            />
            <button
                v-if="isSidebarDesktop"
                class="btn btn-sm collapse-toggle"
                :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
                @click="toggleCollapsed"
            >
                <i :class="['fas', isCollapsed ? 'fa-chevron-right' : 'fa-chevron-left']"></i>
            </button>
        </div>

        <div v-if="!isSidebarDesktop" class="mb-2">
            <SideNavItem
                name="Search Forkto"
                icon="search"
                action="command-search"
                data-cy="mobile-nav-search"
                :is-mobile="true"
                :block="true"
                @click="hideNavCanvas"
                @action="handleAction"
            />
        </div>

        <template v-for="section in visibleNavSections" :key="section.id">
            <div
                class="section-wrapper position-relative"
                :data-sidebar-section="section.id"
                @mouseenter="onSectionEnter(section, $event)"
                @mouseleave="onSectionLeave()"
            >
                <div :class="sectionContainerClasses(section)">
                    <div
                        v-if="section.title && !isCollapsed"
                        :class="[
                            'sidebar-section-title',
                            isSidebarDesktop
                                ? 'sidebar-section-title--desktop text-start mb-2'
                                : 'sidebar-section-title--mobile'
                        ]"
                    >
                        {{ section.title }}
                    </div>

                    <div :class="rowClassesFor(section)">
                        <SideNavItem
                            v-for="item in section.items"
                            :key="item.id"
                            :name="item.label"
                            :icon="item.icon"
                            :route="item.route || null"
                            :path="item.path || null"
                            :href="item.href || null"
                            :action="item.action || null"
                            :data-cy="item.dataCy || null"
                            :is-mobile="!isSidebarDesktop"
                            :block="section.layout === 'stack'"
                            :collapsed="isCollapsed"
                            :badge="item.id === 'chat' ? chatUnreadCount : 0"
                            @click="hideNavCanvas"
                            @action="handleAction"
                        />
                    </div>
                </div>

                <template v-if="section.id === 'dashboard' && hasAnySwitcher">
                    <div :class="isCollapsed ? '' : 'context-bar'">
                        <LocationSwitcher
                            v-if="multipleLocations"
                            :use-custom-dropdown="true"
                            :collapsed="isCollapsed"
                            class="mb-2"
                        />
                        <DepartmentSwitcher
                            v-if="multipleDepartments"
                            :use-custom-dropdown="true"
                            :collapsed="isCollapsed"
                            class="mb-2"
                        />
                        <UserSwitcher
                            v-if="!isSidebarDesktop && hasFeatureFlag(Flags.EasyUserSwitch)"
                            :use-custom-dropdown="true"
                            :collapsed="isCollapsed"
                            class="mb-2"
                        />
                    </div>
                </template>
            </div>
        </template>

        <div
            v-if="$store.state.isMobileOS && appVersion"
            class="sidebar-version text-center small py-3"
        >
            Version {{ appVersion }}
        </div>
    </div>

    <Transition name="sidebar-scroll-cue">
        <div
            v-if="isSidebarDesktop && canScrollUp"
            class="sidebar-scroll-edge-cue sidebar-scroll-top-cue"
        >
            <button
                type="button"
                class="sidebar-scroll-edge-cue__button"
                title="More navigation above"
                aria-label="Scroll sidebar to top"
                @click="scrollSidebarToTop"
            >
                <i class="fas fa-chevron-up" aria-hidden="true"></i>
            </button>
        </div>
    </Transition>

    <Transition name="sidebar-scroll-cue">
        <div
            v-if="isSidebarDesktop && canScrollDown"
            class="sidebar-scroll-edge-cue sidebar-scroll-bottom-cue"
        >
            <button
                type="button"
                class="sidebar-scroll-edge-cue__button"
                title="More navigation below"
                aria-label="Scroll sidebar to bottom"
                @click="scrollSidebarToBottom"
            >
                <i class="fas fa-chevron-down" aria-hidden="true"></i>
            </button>
        </div>
    </Transition>

    <Teleport v-if="isCollapsed" to="body">
        <div
            v-if="flyoutSection"
            ref="flyout"
            class="sidebar-flyout"
            :style="flyoutStyle"
            @mouseenter="cancelLeave"
            @mouseleave="scheduleLeave"
        >
            <div class="flyout-title">{{ flyoutSection.title }}</div>
            <div class="d-flex flex-column gap-1">
                <SideNavItem
                    v-for="item in flyoutSection.items"
                    :key="item.id"
                    :name="item.label"
                    :icon="item.icon"
                    :route="item.route || null"
                    :path="item.path || null"
                    :href="item.href || null"
                    :action="item.action || null"
                    :is-mobile="false"
                    :block="false"
                    :collapsed="false"
                    :badge="item.id === 'chat' ? chatUnreadCount : 0"
                    @click="onFlyoutItemClick"
                    @action="handleAction"
                />
            </div>
        </div>
    </Teleport>
</template>

<script>
import { useChatSummary } from "@/composables/queries/useChat";
import { useScopeChoices } from "@/composables/useScopeChoices";
import { openSupport } from "@/services/openSupport";
import { API } from "@/services/apiInstance";
import DepartmentSwitcher from "../DepartmentSwitcher.vue";
import LocationSwitcher from "../LocationSwitcher.vue";
import UserSwitcher from "../UserSwitcher.vue";
import SideNavItem from "./SideNavItem.vue";
import { visibleSections } from "./sidebarConfig.js";
import { SCAN_LABEL_OPEN } from "@/composables/useUniversalRecordScan";
import textLogoBlack from "@/assets/TextLogoBlack.svg";

import { Flags, hasFeatureFlag, ensureFlagsAreSet } from "@/classes/FeatureFlags";
import { userIsRole } from "@/services/AuthService";
import { App } from "@capacitor/app";

const FLYOUT_ENTER_DELAY = 120;
const FLYOUT_LEAVE_DELAY = 200;
const DESKTOP_UTILITY_SECTION_IDS = new Set(["settings", "support", "profile"]);

export default {
    components: {
        LocationSwitcher,
        DepartmentSwitcher,
        UserSwitcher,
        SideNavItem
    },
    emits: ["hideCanvas"],

    setup() {
        const { count } = useChatSummary();
        return { chatUnreadCount: count, ...useScopeChoices() };
    },

    data() {
        return {
            textLogoBlack,
            appVersion: null,
            flyoutSection: null,
            flyoutStyle: {},
            enterTimer: null,
            leaveTimer: null,
            canScrollUp: false,
            canScrollDown: false
        };
    },
    computed: {
        isSidebarDesktop() {
            return !this.$store.state.isMobile;
        },
        sidebarCollapsed() {
            return this.$store.state.sidebarCollapsed;
        },
        isCollapsed() {
            return this.isSidebarDesktop && this.sidebarCollapsed;
        },
        canAccessDevMode() {
            return this.$store.getters.canAccessDevMode();
        },
        visibleNavSections() {
            const sections = visibleSections({
                has: (flag) => hasFeatureFlag(flag),
                role: (name) => userIsRole(name),
                dev: this.canAccessDevMode,
                isMobile: this.$store.state.isMobile,
                isMobileOS: this.$store.state.isMobileOS,
                isCollapsed: this.isCollapsed
            });
            if (this.isSidebarDesktop) return sections;
            // Keep account utilities together; the app promotion is the last entry.
            return [
                ...sections.filter((section) => section.id !== "promo"),
                ...sections.filter((section) => section.id === "promo")
            ];
        },
        hasAnySwitcher() {
            return (
                this.multipleLocations ||
                this.multipleDepartments ||
                (!this.isSidebarDesktop && hasFeatureFlag(Flags.EasyUserSwitch))
            );
        }
    },
    watch: {
        "$route.fullPath"() {
            this.scheduleActiveItemReveal();
        },
        isSidebarDesktop(value) {
            if (value) {
                this.hideNavCanvas();
                this.scheduleActiveItemReveal();
            }
        },
        isCollapsed(value) {
            if (!value) this.clearFlyout();
            this.scheduleActiveItemReveal();
        }
    },
    async mounted() {
        await ensureFlagsAreSet();
        this.scheduleActiveItemReveal();

        if (this.$store.state.isMobileOS) {
            try {
                const info = await App.getInfo();
                this.appVersion = info.version;
            } catch (error) {
                console.error("Failed to get app version:", error);
            }
        }
    },
    unmounted() {
        this.clearFlyout();
    },
    methods: {
        scheduleActiveItemReveal() {
            if (!this.isSidebarDesktop) return;
            this.$nextTick(() => {
                this.revealActiveItem();
                this.updateScrollCue();
            });
        },
        revealActiveItem() {
            const root = this.$refs.sideNavContent;
            const scrollContainer =
                root && root.scrollHeight > root.clientHeight
                    ? root
                    : root?.closest("#sidebarColumn");
            const activeItem = root?.querySelector(".side-nav-item--desktop.router-link-active");
            if (!scrollContainer || !activeItem) return;

            const containerRect = scrollContainer.getBoundingClientRect();
            const headerRect = this.$refs.sidebarHeader?.getBoundingClientRect();
            const activeRect = activeItem.getBoundingClientRect();
            const visibleTop = Math.max(containerRect.top, headerRect?.bottom || 0) + 8;
            const visibleBottom = containerRect.bottom - 8;

            if (activeRect.top < visibleTop) {
                scrollContainer.scrollTop += activeRect.top - visibleTop;
            } else if (activeRect.bottom > visibleBottom) {
                scrollContainer.scrollTop += activeRect.bottom - visibleBottom;
            }
        },
        updateScrollCue(event) {
            const scrollContainer = event?.currentTarget || this.$refs.sideNavContent;
            const dashboardItem = scrollContainer?.querySelector(
                '[data-sidebar-section="dashboard"] .side-nav-item--desktop'
            );
            const dashboardHasLeftViewport = dashboardItem
                ? dashboardItem.getBoundingClientRect().bottom <=
                  scrollContainer.getBoundingClientRect().top + 4
                : scrollContainer?.scrollTop > 48;

            this.canScrollUp = !!(
                this.isSidebarDesktop &&
                scrollContainer &&
                scrollContainer.scrollTop > 4 &&
                dashboardHasLeftViewport
            );
            this.canScrollDown = !!(
                this.isSidebarDesktop &&
                scrollContainer &&
                scrollContainer.scrollHeight -
                    scrollContainer.clientHeight -
                    scrollContainer.scrollTop >
                    4
            );
        },
        scrollSidebarToTop() {
            const scrollContainer = this.$refs.sideNavContent;
            if (!scrollContainer) return;
            scrollContainer.scrollTop = 0;
            this.canScrollUp = false;
        },
        scrollSidebarToBottom() {
            const scrollContainer = this.$refs.sideNavContent;
            if (!scrollContainer) return;
            scrollContainer.scrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
            this.canScrollDown = false;
        },
        toggleCollapsed() {
            this.$store.commit("setSidebarCollapsed", !this.sidebarCollapsed);
        },
        rowClassesFor() {
            return ["d-flex flex-column gap-1"];
        },
        sectionContainerClasses(section) {
            if (this.isCollapsed) return ["mb-2"];
            if (this.isSidebarDesktop) {
                const isUtilitySection = DESKTOP_UTILITY_SECTION_IDS.has(section.id);
                const firstUtilitySection = this.visibleNavSections.find((candidate) =>
                    DESKTOP_UTILITY_SECTION_IDS.has(candidate.id)
                );

                return [
                    "sidebar-section",
                    `sidebar-section--${section.id}`,
                    isUtilitySection ? "sidebar-section--utility" : "mb-3",
                    isUtilitySection && firstUtilitySection?.id === section.id
                        ? "sidebar-section--utility-first"
                        : "",
                    section.id === "profile" ? "sidebar-section--account" : "",
                    section.variant === "warning" ? "text-bg-warning" : ""
                ];
            }
            const isUtility = DESKTOP_UTILITY_SECTION_IDS.has(section.id);
            const firstUtility = this.visibleNavSections.find((candidate) =>
                DESKTOP_UTILITY_SECTION_IDS.has(candidate.id)
            );
            return [
                "sidebar-section",
                `sidebar-section--${section.id}`,
                isUtility ? "sidebar-section--utility" : "mb-3",
                isUtility && firstUtility?.id === section.id ? "sidebar-section--utility-first" : ""
            ];
        },
        hasFlyout(section) {
            // Only multi-item sections with a title show a flyout.
            // Single-item or untitled sections get tooltips instead.
            return this.isCollapsed && !!section.title && section.items.length > 1;
        },
        onSectionEnter(section, evt) {
            if (!this.isCollapsed) return;
            this.cancelLeave();
            if (!this.hasFlyout(section)) {
                this.clearFlyout();
                return;
            }
            this.cancelEnter();
            const target = evt?.currentTarget;
            if (this.flyoutSection && this.flyoutSection.id !== section.id) {
                this.showFlyout(section, target);
                return;
            }
            this.enterTimer = setTimeout(() => {
                this.showFlyout(section, target);
            }, FLYOUT_ENTER_DELAY);
        },
        onSectionLeave() {
            if (!this.isCollapsed) return;
            this.cancelEnter();
            this.scheduleLeave();
        },
        scheduleLeave() {
            this.cancelLeave();
            this.leaveTimer = setTimeout(() => {
                this.clearFlyout();
            }, FLYOUT_LEAVE_DELAY);
        },
        cancelEnter() {
            if (this.enterTimer) {
                clearTimeout(this.enterTimer);
                this.enterTimer = null;
            }
        },
        cancelLeave() {
            if (this.leaveTimer) {
                clearTimeout(this.leaveTimer);
                this.leaveTimer = null;
            }
        },
        showFlyout(section, anchorEl) {
            if (!anchorEl) return;
            const rect = anchorEl.getBoundingClientRect();
            this.flyoutSection = section;
            this.flyoutStyle = {
                position: "fixed",
                top: `${Math.max(rect.top, 8)}px`,
                left: `${rect.right + 4}px`,
                zIndex: 20100
            };
        },
        clearFlyout() {
            this.cancelEnter();
            this.cancelLeave();
            this.flyoutSection = null;
        },
        onFlyoutItemClick() {
            this.clearFlyout();
            this.hideNavCanvas();
        },
        handleAction(action) {
            if (action === "logout") return this.logout();
            if (action === "support") return this.openSupport();
            if (action === "command-search") {
                this.hideNavCanvas();
                return this.emitter.emit("command-palette:open");
            }
            if (action === "scan-label") {
                this.hideNavCanvas();
                return this.emitter.emit(SCAN_LABEL_OPEN);
            }
        },
        openSupport() {
            openSupport();
        },
        async logout() {
            this.hideNavCanvas();
            await API.logoutUser();
        },
        hideNavCanvas() {
            this.$emit("hideCanvas");
        }
    }
};
</script>

<style scoped>
.side-nav-close {
    min-width: 44px;
    min-height: 44px;
}
.side-nav-content {
    min-height: 100%;
    color: var(--side-nav-text);
    background: var(--side-nav-bg);
}

.side-nav-content--desktop-light {
    --side-nav-bg: var(--forkto-surface);
    --side-nav-text: var(--forkto-ink-2);
    --side-nav-muted: var(--forkto-ink-3);
    --side-nav-hover: var(--forkto-sunk);
    --side-nav-active: var(--forkto-accent-soft);
    --side-nav-active-text: var(--forkto-ink-1);
    --side-nav-border: var(--forkto-line);
    --side-nav-control-bg: var(--forkto-sunk);
    --side-nav-control-border: var(--forkto-line);
    --side-nav-control-text: var(--forkto-ink-1);
    --side-nav-control-height: 3rem;
    --side-nav-control-label-size: 0.68rem;
    --side-nav-control-label-top: 0.4rem;
    --side-nav-control-value-size: 0.9rem;
    --side-nav-control-value-padding-top: 1.15rem;
    --side-nav-control-value-padding-bottom: 0.2rem;
    --side-nav-content-inset: 0.5rem;
    --side-nav-select-indicator: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%235f6a6e' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
    border-right: 1px solid var(--side-nav-border);
}

.side-nav-content--mobile-light {
    --side-nav-bg: var(--forkto-app-ground);
    --side-nav-text: var(--forkto-ink-2);
    --side-nav-muted: var(--forkto-ink-3);
    --side-nav-hover: var(--forkto-sunk);
    --side-nav-active: var(--forkto-surface);
    --side-nav-active-text: var(--forkto-ink-1);
    --side-nav-border: var(--forkto-line-strong);
    --side-nav-control-bg: var(--forkto-surface);
    --side-nav-control-border: var(--forkto-line-strong);
    --side-nav-control-text: var(--forkto-ink-1);
    --side-nav-control-height: 3.625rem;
    --side-nav-control-label-size: 0.8rem;
    --side-nav-control-label-top: 0.65rem;
    --side-nav-control-value-size: 1rem;
    --side-nav-control-value-padding-top: 1.55rem;
    --side-nav-control-value-padding-bottom: 0.35rem;
    --side-nav-content-inset: 0.75rem;
    --side-nav-select-indicator: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%235f6a6e' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
}

.side-nav-content--mobile {
    padding-top: 0 !important;
    padding-bottom: max(var(--forkto-safe-area-bottom), 1rem);
}

.side-nav-content--mobile .sidebar-header {
    position: sticky;
    top: 0;
    z-index: 4;
    padding-top: calc(var(--forkto-safe-area-top) + 0.5rem);
    background: var(--side-nav-bg);
}

.side-nav-content--mobile .sidebar-logo-link {
    min-height: 44px;
}

.side-nav-content--mobile :is(.sidebar-logo-link, .side-nav-close):focus-visible,
.side-nav-content--mobile :deep(select.customdropdown:focus-visible) {
    outline: 2px solid var(--forkto-ink-3);
    outline-offset: 2px;
    box-shadow: none;
}

.sidebar-section-title--mobile {
    margin: 0.75rem 0 0.375rem;
    padding-inline: var(--side-nav-content-inset);
    color: var(--side-nav-muted);
    font-size: 0.875rem;
    font-weight: 600;
}

.side-nav-content--mobile .context-bar {
    margin-bottom: 1rem;
}

.side-nav-content--mobile .sidebar-section--promo {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--side-nav-border);
}

.side-nav-content--mobile .sidebar-section--promo :deep(.side-nav-item) {
    color: var(--side-nav-muted);
    font-size: 0.875rem;
}

.side-nav-content :deep(.sidebar-context-select) {
    position: relative;
}

.side-nav-content :deep(.sidebar-context-select select.customdropdown.form-select) {
    height: var(--side-nav-control-height);
    min-height: var(--side-nav-control-height);
    padding-top: var(--side-nav-control-value-padding-top);
    padding-right: 2rem;
    padding-bottom: var(--side-nav-control-value-padding-bottom);
    padding-left: var(--side-nav-content-inset);
    font-size: var(--side-nav-control-value-size);
    font-weight: 600;
    line-height: 1.2;
}

.side-nav-content :deep(.sidebar-context-select .sidebar-switcher-label) {
    position: absolute;
    z-index: 2;
    top: var(--side-nav-control-label-top);
    left: var(--side-nav-content-inset);
    color: var(--side-nav-muted);
    font-size: var(--side-nav-control-label-size);
    line-height: 1;
    pointer-events: none;
}

.logo-image {
    max-width: 100%;
    height: auto;
}

.sidebar-logo-link {
    display: inline-flex;
    align-items: center;
    max-width: calc(100% - 2.5rem);
    color: inherit;
    border-radius: var(--forkto-radius-compact);
    text-decoration: none;
}

.sidebar-logo-link:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--forkto-focus-ring);
}

.collapse-toggle {
    min-width: 2rem;
    min-height: 2rem;
    color: var(--side-nav-muted);
    background: transparent;
    border: none;
    border-radius: var(--forkto-radius-compact);
}

.collapse-toggle:hover,
.collapse-toggle:focus-visible {
    color: var(--side-nav-active-text);
    background: var(--side-nav-hover);
}

.collapse-toggle:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--forkto-focus-ring);
}

.side-nav-content :deep(select.customdropdown:focus) {
    border-color: var(--side-nav-control-border, var(--forkto-line-strong));
    outline: none;
    box-shadow: 0 0 0 3px var(--forkto-focus-ring);
}

.context-bar {
    margin-bottom: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--side-nav-border);
}

.side-nav-content--desktop-light .context-bar {
    margin-bottom: 1rem;
    padding-top: 1rem;
}

.sidebar-header:not(.justify-content-center) {
    border-bottom: 1px solid var(--side-nav-border);
}

.side-nav-content--desktop-light .sidebar-header {
    position: sticky;
    z-index: 4;
    top: 0.5rem;
    background: var(--side-nav-bg);
    box-shadow: 0 -0.5rem 0 var(--side-nav-bg);
}

.sidebar-section-title--desktop {
    padding-inline-start: var(--side-nav-content-inset);
    color: var(--side-nav-muted);
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.075em;
    line-height: 1.2;
    text-transform: uppercase;
}

.side-nav-content--desktop-light .sidebar-section--dashboard {
    margin-bottom: 0.5rem !important;
}

.sidebar-section--utility {
    margin-bottom: 0.25rem;
}

.sidebar-section--utility-first {
    margin-top: 0.25rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--side-nav-border);
}

.sidebar-section--account {
    margin-top: 0.25rem;
}

.sidebar-version {
    color: var(--side-nav-muted);
}

.sidebar-scroll-edge-cue {
    position: absolute;
    z-index: 6;
    right: 0;
    left: 0;
    display: flex;
    justify-content: center;
    height: 2rem;
    pointer-events: none;
}

.sidebar-scroll-edge-cue::before {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--forkto-app-ground);
}

.sidebar-scroll-top-cue {
    top: 0;
    align-items: flex-start;
}

.sidebar-scroll-top-cue::before {
    -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 50%, transparent 100%);
    mask-image: linear-gradient(to bottom, #000 0%, #000 50%, transparent 100%);
}

.sidebar-scroll-bottom-cue {
    bottom: 3rem;
    align-items: flex-end;
}

.sidebar-scroll-bottom-cue::before {
    -webkit-mask-image: linear-gradient(to top, #000 0%, #000 50%, transparent 100%);
    mask-image: linear-gradient(to top, #000 0%, #000 50%, transparent 100%);
}

.sidebar-scroll-cue-enter-active,
.sidebar-scroll-cue-leave-active {
    transition: opacity 220ms ease-out;
}

.sidebar-scroll-cue-enter-from,
.sidebar-scroll-cue-leave-to {
    opacity: 0;
}

.sidebar-scroll-edge-cue__button {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.125rem;
    padding: 0;
    color: var(--forkto-ink-3);
    font-size: 0.625rem;
    line-height: 1;
    background: transparent;
    border: 0;
    border-radius: var(--forkto-radius-compact);
    opacity: 0.72;
    pointer-events: auto;
}

.sidebar-scroll-edge-cue__button:hover {
    color: var(--forkto-ink-1);
    opacity: 1;
}

.sidebar-scroll-edge-cue__button:focus-visible {
    color: var(--forkto-ink-1);
    outline: 2px solid var(--forkto-focus-ring);
    outline-offset: 1px;
    opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
    .sidebar-scroll-cue-enter-active,
    .sidebar-scroll-cue-leave-active {
        transition: none;
    }
}

.collapsed-rail .section-wrapper + .section-wrapper {
    position: relative;
    padding-top: 0.5rem;
}

.collapsed-rail .section-wrapper + .section-wrapper::before {
    content: "";
    position: absolute;
    top: 0;
    left: 12%;
    right: 12%;
    height: 1px;
    background: var(--side-nav-border);
}
</style>

<style>
.sidebar-flyout {
    --side-nav-text: var(--forkto-ink-2);
    --side-nav-muted: var(--forkto-ink-3);
    --side-nav-hover: var(--forkto-sunk);
    --side-nav-active: var(--forkto-accent-soft);
    --side-nav-active-text: var(--forkto-ink-1);
    background-color: var(--forkto-surface);
    border: 1px solid var(--forkto-line);
    border-radius: var(--forkto-radius-control);
    padding: 0.75rem;
    min-width: 220px;
    width: max-content;
    max-width: 360px;
    box-shadow: 0 12px 32px rgba(var(--forkto-shadow-rgb), 0.16);
}

.sidebar-flyout .flyout-title {
    color: var(--forkto-ink-3);
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.075em;
    margin-bottom: 0.5rem;
    padding: 0 0.5rem;
}
</style>
